import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { PersonContract, PersonContracts } from '~/data/employeeProfile/types';
import {
  EmployeeReadApiClient,
  People,
  fetchFromOldApi,
} from '~/services/Http';

export interface EmployeeProfilePayload {
  profile: People.EmployeeProfileDto;
  contracts: PersonContract[];
}

export interface EmployeeProfileState {
  profile?: People.EmployeeProfileDto;
  contracts?: PersonContract[];
  isLoading: boolean;
  failedToLoad: boolean;
  hasData: boolean;
}

const initialState = {
  isLoading: false,
  failedToLoad: false,
  hasData: false,
};

export interface StoreState {
  employeeProfile: EmployeeProfileState;
}

const initialEmployeeProfileStoreState: StoreState = {
  employeeProfile: { ...initialState },
};

export enum EmployeeDetailsActionKeyPrefixes {
  FETCH_EMPLOYEE_PROFILE = 'employee/FETCH_EMPLOYEE_PROFILE',
  REMOVE_PROFILE_IMAGE = 'employee/REMOVE_PROFILE_IMAGE',
  UPDATE_PROFILE_IMAGE = 'employee/UPDATE_PROFILE_IMAGE',
}

export const getPersonByEmployeeId = async (empId: string) =>
  fetchFromOldApi(`/json/employee/${empId}/profile`).then((r) => r.json());

export const getPersonContracts = async (
  personId: string,
): Promise<PersonContracts> =>
  fetchFromOldApi(`/json/persons/${personId}/contracts`).then((r) => r.json());

export const getPublicProfile = async (
  employeeId: number | string,
): Promise<People.EmployeeProfileDto> => {
  return EmployeeReadApiClient.getPublicProfile(
    `${employeeId}`,
    undefined,
  ).then((r) => r.data);
};

const emptyPersonContracts = {
  contracts: [],
  profile: {
    firstname: '',
    lastname: '',
  },
};

export const getPersonContractsByEmployeeId = async (
  empId: string,
): Promise<PersonContracts> => {
  return getPersonByEmployeeId(empId)
    .then((person) => getPersonContracts(person.fEmpId))
    .catch((err) => {
      return emptyPersonContracts;
    });
};

export const deleteProfileImage = async (
  employeeId: number | string,
  profilePictureId: string,
): Promise<unknown> => {
  return await fetchFromOldApi(
    `/json/employee/${employeeId}/profile/picture/${profilePictureId}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    },
  ).then((r) => r.json());
};

export const setProfileImage = async (
  employeeId: number | string,
  blob: Blob,
): Promise<{ fEmpId: number; fEmpProfPictureId: string }> => {
  const fd = new FormData();
  fd.append('picture', blob);

  return await fetchFromOldApi(`/form/employee/${employeeId}/profile/picture`, {
    method: 'PUT',
    body: fd,
  }).then((r) => r.json());
};

export const fetchEmployeeDetails = createAsyncThunk<
  EmployeeProfilePayload,
  string
>(
  EmployeeDetailsActionKeyPrefixes.FETCH_EMPLOYEE_PROFILE,
  async (employeeId: string, thunkAPI): Promise<EmployeeProfilePayload> => {
    const profile = await getPublicProfile(employeeId);
    const contracts: PersonContracts = await getPersonContractsByEmployeeId(
      profile.id.toString(),
    );

    return {
      ...contracts,
      profile: profile,
    };
  },
);

type EmployeeProfileUpdatePayload = Pick<EmployeeProfilePayload, 'profile'>;

type UpdateProfileImageProps = {
  employeeId: number | string;
  blob: Blob;
};

export const updateProfileImage = createAsyncThunk<
  EmployeeProfileUpdatePayload,
  UpdateProfileImageProps
>(
  EmployeeDetailsActionKeyPrefixes.UPDATE_PROFILE_IMAGE,
  async ({
    employeeId,
    blob,
  }: UpdateProfileImageProps): Promise<EmployeeProfileUpdatePayload> => {
    await setProfileImage(employeeId, blob);
    const profile = await getPublicProfile(employeeId);

    return {
      profile,
    };
  },
);

type RemoveProfileImageProps = {
  employeeId: number | string;
  profilePictureId: string;
};

export const removeProfileImage = createAsyncThunk<
  EmployeeProfileUpdatePayload,
  RemoveProfileImageProps
>(
  EmployeeDetailsActionKeyPrefixes.REMOVE_PROFILE_IMAGE,
  async ({
    employeeId,
    profilePictureId,
  }: RemoveProfileImageProps): Promise<EmployeeProfileUpdatePayload> => {
    await deleteProfileImage(employeeId, profilePictureId);
    const profile = await getPublicProfile(employeeId);

    return {
      profile,
    };
  },
);

const reducerProfilePicChange = {
  fulfilled: (
    state: StoreState,
    action: PayloadAction<EmployeeProfileUpdatePayload>,
  ) => {
    state.employeeProfile = {
      ...state.employeeProfile,
      ...action.payload,
      ...initialState,
      hasData: true,
    };
  },
  pending: (
    state: StoreState,
    action: PayloadAction<EmployeeProfileUpdatePayload>,
  ) => {
    state.employeeProfile = {
      ...state.employeeProfile,
      ...initialState,
      isLoading: true,
      hasData: state.employeeProfile.hasData,
    };
  },
  rejected: (
    state: StoreState,
    action: PayloadAction<EmployeeProfileUpdatePayload>,
  ) => {
    state.employeeProfile = {
      ...state.employeeProfile,
      ...initialState,
      failedToLoad: true,
      hasData: state.employeeProfile.hasData,
    };
  },
};

export const employeeProfileSlice = createSlice({
  name: '~/components/Employee',
  initialState: initialEmployeeProfileStoreState,
  reducers: {},
  extraReducers: {
    [fetchEmployeeDetails.fulfilled.type]: (
      state: StoreState,
      action: PayloadAction<EmployeeProfilePayload>,
    ) => {
      state.employeeProfile = {
        ...initialState,
        ...action.payload,
        hasData: true,
      };
    },
    [fetchEmployeeDetails.pending.type]: (
      state: StoreState,
      action: PayloadAction<EmployeeProfilePayload>,
    ) => {
      state.employeeProfile = {
        ...initialState,
        isLoading: true,
      };
    },
    [fetchEmployeeDetails.rejected.type]: (
      state: StoreState,
      action: PayloadAction<EmployeeProfilePayload>,
    ) => {
      state.employeeProfile = {
        ...initialState,
        failedToLoad: true,
      };
    },
    [updateProfileImage.fulfilled.type]: reducerProfilePicChange.fulfilled,
    [updateProfileImage.pending.type]: reducerProfilePicChange.pending,
    [updateProfileImage.rejected.type]: reducerProfilePicChange.rejected,
    [removeProfileImage.fulfilled.type]: reducerProfilePicChange.fulfilled,
    [removeProfileImage.pending.type]: reducerProfilePicChange.pending,
    [removeProfileImage.rejected.type]: reducerProfilePicChange.rejected,
  },
});

export const employeeProfileReducer = employeeProfileSlice.reducer;
